/**
 * Page 2 of Create trigger drawer
 * Configure job
 * Select job artifact, alias/version, config, target project and queue
 */
import {useNodeValue} from '@wandb/weave-ui/cgreact';
import React, {useCallback, useEffect} from 'react';
import {Loader} from 'semantic-ui-react';

import {trackLaunchConfigUpdated} from '../../../../util/navigation';
import {Select} from '../../../Form/Select';
import {getJobDefinitionFileOp, JobFile} from '../../JobDetail/utilsJobFile';
import {JobDetails, JobRun} from '../../JobDetail/utilTypes';
import {JobDrawerOverrides} from '../../JobDrawer/JobDrawerOverrides';
import {JobOption, SelectJob} from '../../SelectJob';
import {SelectJobVersion} from '../../SelectJobVersion';
import {
  getDefaultQueue,
  QueueOption,
  QueueOptions,
  SelectQueue,
} from '../../SelectQueue';
import {extractConfig} from '../../utils';
import {JsonObject} from '../../utilTypes';
import {ConfigKey} from '../ConfigKey';
import {Trigger} from '../TriggerEventTypes';
import {OptionType} from '../utils';
import * as S from './CreateTriggerDrawerLoaded.styles';

type DrawerPageJobConfigurationProps = {
  entityName: string;
  projectName: string;
  trigger?: Trigger;
  queuedJobID: JobOption | null;
  queuedJob?: JobDetails;
  queuedJobAlias?: string;
  jobDetailsMap: Record<string, JobDetails>;
  setQueuedJobID: (val: JobOption) => void;
  setQueuedJob: (val: JobDetails) => void;
  setQueuedJobAlias: (val: string) => void;
  overrides: string;
  overridesError: string;
  onOverridesChange: (val: string) => void;
  setOverridesError: (val: string) => void;
  customRunColors: Record<string, string>;
  allProjects: string[];
  targetProject: string;
  setTargetProject: (val: string) => void;
  queues: QueueOptions;
  selectedQueue: QueueOption | null;
  setSelectedQueue: (val: QueueOption) => void;
};

export const DrawerPageJobConfiguration = ({
  entityName,
  projectName,
  trigger,
  queuedJobID,
  queuedJob,
  queuedJobAlias,
  jobDetailsMap,
  setQueuedJobID,
  setQueuedJob,
  setQueuedJobAlias,
  overrides,
  overridesError,
  onOverridesChange,
  setOverridesError,
  customRunColors,
  allProjects,
  targetProject,
  setTargetProject,
  queues,
  selectedQueue,
  setSelectedQueue,
}: DrawerPageJobConfigurationProps) => {
  const onSelectJob = (jobOption: JobOption) => {
    setQueuedJobID(jobOption);
    const job = jobDetailsMap[jobOption.label];
    if (job && job.versions.length !== 0) {
      setQueuedJob(job);
      setQueuedJobAlias(job.versionInfo.runTarget ?? '');
    }
  };
  const previousRuns = queuedJob?.runs.filter(
    run => run.version === queuedJob?.versionInfo?.runTargetNum
  );

  // Overrides
  // Get overrides template
  const contents = getJobDefinitionFileOp(
    entityName,
    queuedJob?.projectName ?? '',
    queuedJob?.name ?? '',
    queuedJob?.versionInfo.runTarget ?? ''
  );
  const {result: jobDefinitionStr, loading: jobFileLoading} =
    useNodeValue(contents);

  // Set config based on overrides, if any
  const setConfig = (config: JsonObject) => {
    const newOverrides = {
      args: [],
      run_config: config,
      entry_point: [],
    };
    const overrideStr = JSON.stringify(newOverrides, null, 4);
    onOverridesChange(overrideStr);
  };

  // Set config based on run selected
  const onSelectRun = (run: JobRun) => {
    const config = extractConfig(run.config);
    setConfig(config);
  };

  useEffect(() => {
    if (!jobDefinitionStr) {
      return;
    }
    const jobFile = new JobFile(jobDefinitionStr);
    setConfig(jobFile.getInputExample());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDefinitionStr]);

  // Launch configuration (target project and queue)
  const projectOptions: OptionType[] = allProjects?.map(name => {
    return {
      label: name,
      value: name,
    };
  });

  // Validate config overrides
  const validate = useCallback(
    (overridesToValidate: string) => {
      let ok = true;
      try {
        JSON.parse(overridesToValidate);
        setOverridesError('');
      } catch (err) {
        setOverridesError('Overrides must be valid JSON');
        ok = false;
      }
      return ok;
    },
    [setOverridesError]
  );

  useEffect(() => {
    validate(overrides);
  }, [overrides, validate]);

  const defaultQueue = getDefaultQueue(queues);

  const onSelectQueue = (option: QueueOption) => {
    if (option !== selectedQueue) {
      // Only track the event if the selected queue was changed
      trackLaunchConfigUpdated({
        eventType: 'Launch config updated via Create Trigger modal',
        configType: 'queue_name',
      });
    }
    setSelectedQueue(option);
  };

  return (
    <>
      <S.PageLabelRow>Step 2 of 3 - Job configuration</S.PageLabelRow>
      <S.Description>
        Select and configure a job to automate. Use the suggested keys to
        reference condition objects in the job parameters.
      </S.Description>
      <S.LabelRow>
        <S.Label>Job</S.Label>
      </S.LabelRow>
      <SelectJob
        entityName={entityName}
        projectName={projectName}
        selectedJob={queuedJobID}
        onSelectJob={onSelectJob}
      />
      {!contents && jobFileLoading && <Loader active />}
      {queuedJob && (
        <>
          <S.LabelRow>
            <S.Label>Job version</S.Label>
          </S.LabelRow>
          <SelectJobVersion
            jobVersions={queuedJob.versions}
            selectedAlias={queuedJobAlias ?? ''}
            onSelectJobAlias={setQueuedJobAlias}
          />
          {contents && !jobFileLoading && (
            <>
              <JobDrawerOverrides
                overrides={overrides}
                onOverridesChange={onOverridesChange}
                previousRuns={previousRuns ?? []}
                onSelectRun={onSelectRun}
                customRunColors={customRunColors}
              />
              {overridesError && <S.Error>{overridesError}</S.Error>}
              <S.PlainText>
                Use the following keys to refer to condition objects in the job
                parameters:
              </S.PlainText>
              {trigger?.templateStrings.map(ts => (
                <S.TemplateHelperRow key={ts.value}>
                  <S.TemplateHelperKey key={`key-${ts.value}`}>
                    <ConfigKey configKey={ts.value} />
                  </S.TemplateHelperKey>
                  <S.TemplateHelperString key={ts.value}>
                    {ts.help}
                  </S.TemplateHelperString>
                </S.TemplateHelperRow>
              ))}
            </>
          )}
        </>
      )}
      {queuedJobAlias && (
        <>
          <S.LabelRow>
            <S.Label>Destination project</S.Label>
          </S.LabelRow>
          <Select<OptionType | null>
            options={projectOptions}
            defaultValue={
              targetProject
                ? {label: targetProject, value: targetProject}
                : null
            }
            placeholder="Select project"
            onChange={(option: OptionType | null) => {
              if (option) {
                setTargetProject(option.value);
                trackLaunchConfigUpdated({
                  eventType: 'Launch config updated via Create Trigger modal',
                  configType: 'project_name',
                });
              }
            }}
          />
          {/*TODO: Add option for resource configuration*/}
          <S.LabelRow>
            <S.Label>Queue</S.Label>
          </S.LabelRow>
          <SelectQueue
            options={queues}
            defaultQueueOption={defaultQueue ?? undefined}
            onSelectQueue={onSelectQueue}
          />
        </>
      )}
    </>
  );
};
